import React, { useEffect, useState } from "react"
import Button from "../../../components/ui/Button"
import Icon from "../../../components/AppIcon"
import { readImageAsBase64 } from "../../../utils/imageHelpers"
import {
  getCredentialTemplates,
  createCredentialTemplate,
  updateCredentialTemplate,
  deleteCredentialTemplate,
  duplicateCredentialTemplate,
  DEFAULT_TEMPLATE_FIELDS,
} from "../../../utils/credentialTemplateHelpers"
import { getReadableTextColor } from "../../../utils/colorHelpers"

const TEMPLATE_TYPES = [
  { value: "certificate", label: "Certificate" },
  { value: "badge", label: "Badge" },
  { value: "micro-credential", label: "Micro-credential" },
  { value: "professional", label: "Professional Credential" },
]

const FONT_OPTIONS = ["Inter", "Playfair Display", "Georgia", "Poppins", "Space Grotesk"]

const PATTERN_STYLES = [
  { value: "diamonds", label: "Diamond Grid" },
  { value: "dots", label: "Dot Grid" },
  { value: "waves", label: "Waves" },
  { value: "circuit", label: "Circuit" },
  { value: "florets", label: "Floral Doodle" },
  { value: "confetti", label: "Confetti Sparkle" },
]

const renderPatternDef = (id, style, color) => {
  switch (style) {
    case "dots":
      return (
        <pattern id={id} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="10" cy="10" r="1.4" fill={color} />
        </pattern>
      )
    case "waves":
      return (
        <pattern id={id} x="0" y="0" width="40" height="20" patternUnits="userSpaceOnUse">
          <path d="M0 10 Q10 0 20 10 T40 10" fill="none" stroke={color} strokeWidth="1" />
        </pattern>
      )
    case "circuit":
      return (
        <pattern id={id} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M0 20 H15 M25 20 H40 M20 0 V15 M20 25 V40" stroke={color} strokeWidth="1" fill="none" />
          <circle cx="20" cy="20" r="2" fill={color} />
        </pattern>
      )
    case "florets":
      return (
        <pattern id={id} x="0" y="0" width="36" height="36" patternUnits="userSpaceOnUse">
          <g fill="none" stroke={color} strokeWidth="1">
            <circle cx="18" cy="10" r="3" />
            <path d="M18 13 v8" />
            <path d="M18 21 q-4 2 -6 6" />
            <path d="M18 21 q4 2 6 6" />
          </g>
        </pattern>
      )
    case "confetti":
      return (
        <pattern id={id} x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
          <circle cx="6" cy="8" r="1" fill={color} />
          <circle cx="20" cy="4" r="1.4" fill={color} />
          <path
            d="M14 18 l1.2 2.4 2.6 0.4 -1.9 1.8 0.5 2.6 -2.4 -1.3 -2.4 1.3 0.5 -2.6 -1.9 -1.8 2.6 -0.4 Z"
            fill={color}
            opacity="0.8"
          />
        </pattern>
      )
    case "diamonds":
    default:
      return (
        <pattern id={id} x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
          <circle cx="16" cy="16" r="1.3" fill={color} />
          <path d="M0 16 L16 0 L32 16 L16 32 Z" fill="none" stroke={color} strokeWidth="0.6" />
        </pattern>
      )
  }
}

const emptyForm = () => ({
  name: "",
  type: "certificate",
   design: {
    logoUrl: "",
    logoPosition: "left", // left | center | right
    primaryColor: "#4F46E5",
    secondaryColor: "#111827",
    backgroundColor: "#FFFFFF",
    fontFamily: "Inter",
    layout: "landscape",
    patternStyle: "diamonds",
    patternColor: "#4F46E5",
    patternOpacity: 0.05,
    useGradientBg: false,
    gradientColor: "#818CF8",
  },
  issuer: { name: "", title: "", signatureUrl: "" },
  fields: DEFAULT_TEMPLATE_FIELDS,
  skills: [],
  evidence: {
    linkedCourseId: "",
    linkedProgramId: "",
    minAssessmentScore: "",
    requireProjectSubmission: false,
  },
  verification: { publicVerification: true, showQr: true },
})

const inputClass =
  "w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground/60 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
const labelClass = "text-sm font-medium text-foreground mb-1.5 block"

const FORM_TABS = [
  { key: "details", label: "Details", icon: "FileText" },
  { key: "design", label: "Design", icon: "Palette" },
  { key: "fields", label: "Fields", icon: "ListChecks" },
  { key: "evidence", label: "Evidence", icon: "ShieldCheck" },
  { key: "verification", label: "Verification", icon: "BadgeCheck" },
]

const CredentialTemplatesPanel = ({ organization, currentUserUid }) => {
  const orgId = organization?.orgId
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState("list") // "list" | "editor"
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [activeFormTab, setActiveFormTab] = useState("details")
  const [saving, setSaving] = useState(false)
  const [skillInput, setSkillInput] = useState("")
  const [error, setError] = useState("")
  const [showFullPreview, setShowFullPreview] = useState(false)

  const loadTemplates = async () => {
    if (!orgId) return
    setLoading(true)
    const { templates: t, error: err } = await getCredentialTemplates(orgId)
    if (err) setError(err)
    setTemplates(t)
    setLoading(false)
  }

  useEffect(() => {
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const openCreate = () => {
    setForm(emptyForm())
    setEditingId(null)
    setActiveFormTab("details")
    setView("editor")
  }

  const openEdit = (template) => {
    const { id, name, type, design, issuer, fields, skills, evidence, verification } = template
    setForm({
      name,
      type,
      design: { ...emptyForm().design, ...design },
      issuer,
      fields: fields?.length ? fields : DEFAULT_TEMPLATE_FIELDS,
      skills: skills || [],
      evidence: evidence || emptyForm().evidence,
      verification: verification || emptyForm().verification,
    })
    setEditingId(id)
    setActiveFormTab("details")
    setView("editor")
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Give this template a name before saving.")
      setActiveFormTab("details")
      return
    }
    setSaving(true)
    setError("")

    const result = editingId
      ? await updateCredentialTemplate(orgId, editingId, form)
      : await createCredentialTemplate(orgId, form, currentUserUid)

    setSaving(false)

    if (result.error) {
      setError(result.error)
      return
    }

    await loadTemplates()
    setView("list")
  }

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return
    await deleteCredentialTemplate(orgId, templateId)
    loadTemplates()
  }

  const handleDuplicate = async (template) => {
    await duplicateCredentialTemplate(orgId, template, currentUserUid)
    loadTemplates()
  }

  const handleToggleStatus = async (template) => {
    const next = template.status === "active" ? "draft" : "active"
    await updateCredentialTemplate(orgId, template.id, { status: next })
    loadTemplates()
  }

  const handleLogoUpload = async (e) => {
    const file = e.target?.files?.[0]
    if (!file) return
    const { base64, error: err } = await readImageAsBase64(file, 250 * 1024)
    if (err) {
      setError(err.message)
      return
    }
    setForm((f) => ({ ...f, design: { ...f.design, logoUrl: base64 } }))
  }

  const addSkill = () => {
    const v = skillInput.trim()
    if (!v || form.skills.includes(v)) return
    setForm((f) => ({ ...f, skills: [...f.skills, v] }))
    setSkillInput("")
  }

  const removeSkill = (skill) => {
    setForm((f) => ({ ...f, skills: f.skills.filter((s) => s !== skill) }))
  }

  const updateField = (index, key, value) => {
    setForm((f) => {
      const fields = [...f.fields]
      fields[index] = { ...fields[index], [key]: value }
      return { ...f, fields }
    })
  }

  const addField = () => {
    setForm((f) => ({
      ...f,
      fields: [...f.fields, { key: `custom_${f.fields.length}`, label: "New Field", source: "custom" }],
    }))
  }

  const removeField = (index) => {
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== index) }))
  }

  const renderCredentialCard = (size = "md") => {
    const textColor = getReadableTextColor(form.design.backgroundColor)
    const mutedTextClass = textColor === "#FFFFFF" ? "opacity-70" : "opacity-60"
    const isLg = size === "lg"
    const padding = isLg ? "p-10" : "p-6"
    const typeSize = isLg ? "text-xs" : "text-[10px]"
    const titleSize = isLg ? "text-3xl" : "text-xl"
    const bodySize = isLg ? "text-base" : "text-sm"
    const nameSize = isLg ? "text-2xl" : "text-lg"
    const skillSize = isLg ? "text-xs px-3 py-1" : "text-[10px] px-2 py-0.5"
    const footerSize = isLg ? "text-xs" : "text-[10px]"
    const sealBox = isLg ? "h-14 w-14" : "h-9 w-9"
    const skillLimit = isLg ? 6 : 3
    const patternId = `cert-pattern-${size}`
    const corners = [
      { pos: "top-2.5 left-2.5", rotate: "0deg" },
      { pos: "top-2.5 right-2.5", rotate: "90deg" },
      { pos: "bottom-2.5 right-2.5", rotate: "180deg" },
      { pos: "bottom-2.5 left-2.5", rotate: "270deg" },
    ]

    return (
      <div
        className="rounded-2xl p-[2px] shadow-lg w-full"
        style={{
          background: `linear-gradient(135deg, ${form.design.primaryColor}, ${form.design.secondaryColor})`,
        }}
      >
               <div
          className={`relative overflow-hidden rounded-[15px] ${padding} flex flex-col gap-3`}
          style={{
            background: form.design.useGradientBg
              ? `linear-gradient(135deg, ${form.design.backgroundColor}, ${form.design.gradientColor})`
              : form.design.backgroundColor,
            fontFamily: form.design.fontFamily,
            aspectRatio: form.design.layout === "landscape" ? "1.6" : "0.72",
            color: textColor,
          }}
        >
          {/* Premium doodle background pattern */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: form.design.patternOpacity }}
            preserveAspectRatio="none"
          >
            <defs>{renderPatternDef(patternId, form.design.patternStyle, form.design.patternColor)}</defs>
            <rect width="100%" height="100%" fill={`url(#${patternId})`} />
          </svg>

                    {/* Corner flourishes */}
          {corners.map((c, i) => (
            <svg
              key={i}
              className={`absolute ${c.pos} pointer-events-none`}
              width={isLg ? 30 : 20}
              height={isLg ? 30 : 20}
              viewBox="0 0 40 40"
              style={{ transform: `rotate(${c.rotate})`, opacity: 0.5 }}
            >
              <path d="M18 2 H2 V18" fill="none" stroke={form.design.primaryColor} strokeWidth="1.6" strokeLinecap="round" />
              <path d="M2 10 Q2 2 10 2" fill="none" stroke={form.design.primaryColor} strokeWidth="1" strokeLinecap="round" opacity="0.7" />
            </svg>
          ))}

          {/* corner seal */}
          <div
            className="absolute -top-6 -right-6 h-20 w-20 rounded-full opacity-10"
            style={{ backgroundColor: form.design.primaryColor }}
          />
          <div
            className={`absolute top-4 right-4 ${sealBox} rounded-full border-2 flex items-center justify-center`}
            style={{ borderColor: form.design.primaryColor }}
          >
            <div
              className="absolute inset-1 rounded-full border pointer-events-none"
              style={{ borderColor: `${form.design.primaryColor}50` }}
            />
            <Icon name="Award" size={isLg ? 24 : 16} style={{ color: form.design.primaryColor }} />
          </div>

          <div className="relative z-10 flex flex-col gap-3 h-full">
            <div
              className={`shrink-0 flex ${
                form.design.logoPosition === "center"
                  ? "justify-center"
                  : form.design.logoPosition === "right"
                  ? "justify-end"
                  : "justify-start"
              }`}
            >
              {form.design.logoUrl ? (
                <img src={form.design.logoUrl} alt="" className={isLg ? "h-14 object-contain" : "h-9 object-contain"} />
              ) : (
                <div
                  className={`${isLg ? "h-14 w-14 text-base" : "h-9 w-9 text-xs"} rounded-full flex items-center justify-center text-white font-bold`}
                  style={{ backgroundColor: form.design.primaryColor }}
                >
                  N
                </div>
              )}
            </div>

            <div className="shrink-0">
              <p className={`${typeSize} font-semibold uppercase tracking-[0.2em]`} style={{ color: form.design.primaryColor }}>
                {TEMPLATE_TYPES.find((t) => t.value === form.type)?.label || "Certificate"}
              </p>
              <h3 className={`${titleSize} font-bold leading-snug mt-1 truncate`} style={{ color: textColor }}>
                {form.name || "Untitled Template"}
              </h3>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden flex flex-col justify-center gap-1.5">
              <p className={`${bodySize} ${mutedTextClass}`} style={{ color: textColor }}>
                This certifies that
              </p>
              <p
                className={`${nameSize} font-semibold truncate`}
                style={{ color: form.design.primaryColor, fontFamily: "Georgia, serif" }}
              >
                Jane Doe
              </p>

              {form.skills.length > 0 && (
                <div className="flex flex-nowrap gap-1.5 overflow-hidden">
                  {form.skills.slice(0, skillLimit).map((s) => (
                    <span
                      key={s}
                      className={`shrink-0 whitespace-nowrap ${skillSize} rounded-full border`}
                      style={{ borderColor: form.design.primaryColor, color: form.design.primaryColor }}
                    >
                      {s}
                    </span>
                  ))}
                  {form.skills.length > skillLimit && (
                    <span
                      className={`shrink-0 whitespace-nowrap ${skillSize} rounded-full border opacity-70`}
                      style={{ borderColor: form.design.primaryColor, color: form.design.primaryColor }}
                    >
                      +{form.skills.length - skillLimit} more
                    </span>
                  )}
                </div>
              )}
            </div>

            <div
              className={`shrink-0 mt-auto flex items-center justify-between ${footerSize} pt-3 border-t`}
              style={{ borderColor: `${textColor}20` }}
            >
              <div className={mutedTextClass} style={{ color: textColor }}>
                <p className="font-medium">{form.issuer.name || "Issuer"}</p>
                <p>{form.issuer.title || " "}</p>
              </div>
              <div className="text-right">
                <p className={mutedTextClass} style={{ color: textColor }}>
                  ID: NSL-XXXXX
                </p>
                <p className="flex items-center gap-1 justify-end mt-0.5 font-medium" style={{ color: form.design.primaryColor }}>
                  <Icon name="ShieldCheck" size={isLg ? 12 : 10} />
                  Powered by Nesolearn
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ---------- LIST VIEW ----------
  if (view === "list") {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground max-w-xl">
            Design the certificates, badges, and micro-credentials your organization issues. Every
            credential issued later is built from one of these templates.
          </p>
          <Button iconName="Plus" onClick={openCreate} className="shrink-0">
            Create Template
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            Loading templates...
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 px-6 text-center">
            <Icon name="Award" size={32} className="mx-auto mb-3 text-muted-foreground" />
            <h3 className="font-semibold text-foreground mb-1">No credential templates yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first template to start issuing credentials.
            </p>
            <Button iconName="Plus" onClick={openCreate}>
              Create Template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div
                key={t.id}
                className="rounded-xl border border-border bg-card overflow-hidden flex flex-col"
              >
                <div
                  className="h-28 flex items-center justify-center relative"
                  style={{ backgroundColor: t.design?.backgroundColor || "#F9FAFB" }}
                >
                  <div
                    className="absolute inset-2 rounded-lg border-2 flex items-center justify-center px-3"
                    style={{ borderColor: t.design?.primaryColor || "#4F46E5" }}
                  >
                    {t.design?.logoUrl ? (
                      <img src={t.design.logoUrl} alt="" className="h-8 object-contain" />
                    ) : (
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: t.design?.primaryColor || "#4F46E5" }}
                      >
                        {t.name || "Untitled"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-foreground text-sm leading-tight">{t.name}</h4>
                    <span
                      className={`shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        t.status === "active"
                          ? "bg-success/10 text-success"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground capitalize">
                    {t.type?.replace("-", " ")}
                  </span>
                  <div className="mt-auto flex items-center gap-1.5 pt-3">
                    <Button size="xs" variant="outline" iconName="Pencil" onClick={() => openEdit(t)}>
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      iconName="Copy"
                      onClick={() => handleDuplicate(t)}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      iconName={t.status === "active" ? "PauseCircle" : "CheckCircle2"}
                      onClick={() => handleToggleStatus(t)}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      iconName="Trash2"
                      onClick={() => handleDelete(t.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ---------- EDITOR VIEW ----------
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => setView("list")}>
          Back
        </Button>
        <h2 className="font-semibold text-foreground">
          {editingId ? "Edit Template" : "New Credential Template"}
        </h2>
      </div>

      {error && (
        <div className="rounded-md bg-error/10 text-error text-sm px-3 py-2">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-6">
        {/* Form column */}
        <div className="space-y-5 min-w-0">
          <div className="flex gap-1 overflow-x-auto border-b border-border pb-px">
            {FORM_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFormTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeFormTab === tab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeFormTab === "details" && (
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Template name</label>
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Certified Blockchain Professional"
                />
              </div>
              <div>
                <label className={labelClass}>Type</label>
                <select
                  className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {TEMPLATE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Issuer name</label>
                  <input
                    className={inputClass}
                    value={form.issuer.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, issuer: { ...f.issuer, name: e.target.value } }))
                    }
                    placeholder="Your organization"
                  />
                </div>
                <div>
                  <label className={labelClass}>Issuer title</label>
                  <input
                    className={inputClass}
                    value={form.issuer.title}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, issuer: { ...f.issuer, title: e.target.value } }))
                    }
                    placeholder="e.g. Director of Learning"
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Skills this credential represents</label>
                <div className="flex gap-2 mb-2">
                  <input
                    className={inputClass}
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                    placeholder="Add a skill and press Enter"
                  />
                  <Button variant="outline" size="sm" onClick={addSkill}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.skills.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1 bg-muted text-foreground text-xs px-2 py-1 rounded-full"
                    >
                      {s}
                      <button onClick={() => removeSkill(s)}>
                        <Icon name="X" size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeFormTab === "design" && (
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Logo</label>
                <div className="flex items-center gap-3 mb-3">
                  {form.design.logoUrl && (
                    <img src={form.design.logoUrl} alt="" className="h-10 w-10 object-contain rounded border border-border" />
                  )}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="text-sm" />
                </div>
                {form.design.logoUrl && (
                  <div>
                    <label className={labelClass}>Logo position</label>
                    <div className="flex gap-1">
                      {["left", "center", "right"].map((pos) => (
                        <button
                          key={pos}
                          onClick={() =>
                            setForm((f) => ({ ...f, design: { ...f.design, logoPosition: pos } }))
                          }
                          className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                            form.design.logoPosition === pos
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["primaryColor", "Primary color"],
                  ["secondaryColor", "Secondary color"],
                  ["backgroundColor", "Background"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className={labelClass}>{label}</label>
                    <input
                      type="color"
                      className="w-full h-10 rounded-md border border-input cursor-pointer"
                      value={form.design[key]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, design: { ...f.design, [key]: e.target.value } }))
                      }
                    />
                  </div>
                               ))}
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={form.design.useGradientBg}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        design: { ...f.design, useGradientBg: e.target.checked },
                      }))
                    }
                  />
                  Use gradient background
                </label>
                {form.design.useGradientBg && (
                  <div>
                    <label className={labelClass}>Gradient second color</label>
                    <input
                      type="color"
                      className="w-full h-10 rounded-md border border-input cursor-pointer"
                      value={form.design.gradientColor}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          design: { ...f.design, gradientColor: e.target.value },
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Blends from Background into this color.
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                <p className="text-sm font-medium text-foreground">Background pattern</p>
                <div>
                  <label className={labelClass}>Pattern style</label>
                  <select
                    className={inputClass}
                    value={form.design.patternStyle}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        design: { ...f.design, patternStyle: e.target.value },
                      }))
                    }
                  >
                    {PATTERN_STYLES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Pattern color</label>
                    <input
                      type="color"
                      className="w-full h-10 rounded-md border border-input cursor-pointer"
                      value={form.design.patternColor}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          design: { ...f.design, patternColor: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Pattern intensity</label>
                    <input
                      type="range"
                      min="0.02"
                      max="0.35"
                      step="0.01"
                      className="w-full mt-2.5"
                      value={form.design.patternOpacity}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          design: { ...f.design, patternOpacity: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Font</label>
                  <select
                    className={inputClass}
                    value={form.design.fontFamily}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, design: { ...f.design, fontFamily: e.target.value } }))
                    }
                  >
                    {FONT_OPTIONS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Layout</label>
                  <select
                    className={inputClass}
                    value={form.design.layout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, design: { ...f.design, layout: e.target.value } }))
                    }
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeFormTab === "fields" && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                These populate the credential when it's issued (learner name, date, etc.).
              </p>
              {form.fields.map((field, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    className={inputClass}
                    value={field.label}
                    onChange={(e) => updateField(i, "label", e.target.value)}
                    placeholder="Field label"
                  />
                  <input
                    className={inputClass}
                    value={field.source}
                    onChange={(e) => updateField(i, "source", e.target.value)}
                    placeholder="Source key"
                  />
                  <Button variant="ghost" size="icon" iconName="Trash2" onClick={() => removeField(i)} />
                </div>
              ))}
              <Button variant="outline" size="sm" iconName="Plus" onClick={addField}>
                Add field
              </Button>
            </div>
          )}

          {activeFormTab === "evidence" && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Optionally require this evidence before a credential using this template can be issued.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Linked course ID</label>
                  <input
                    className={inputClass}
                    value={form.evidence.linkedCourseId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        evidence: { ...f.evidence, linkedCourseId: e.target.value },
                      }))
                    }
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className={labelClass}>Linked program ID</label>
                  <input
                    className={inputClass}
                    value={form.evidence.linkedProgramId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        evidence: { ...f.evidence, linkedProgramId: e.target.value },
                      }))
                    }
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Minimum assessment score (%)</label>
                <input
                  type="number"
                  className={inputClass}
                  value={form.evidence.minAssessmentScore}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      evidence: { ...f.evidence, minAssessmentScore: e.target.value },
                    }))
                  }
                  placeholder="e.g. 80"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.evidence.requireProjectSubmission}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      evidence: { ...f.evidence, requireProjectSubmission: e.target.checked },
                    }))
                  }
                />
                Require a submitted project as evidence
              </label>
            </div>
          )}

          {activeFormTab === "verification" && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.verification.publicVerification}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      verification: { ...f.verification, publicVerification: e.target.checked },
                    }))
                  }
                />
                Allow public verification (no login required)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.verification.showQr}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      verification: { ...f.verification, showQr: e.target.checked },
                    }))
                  }
                />
                Include a QR code on the credential
              </label>
              <div className="rounded-md bg-muted/50 text-xs text-muted-foreground px-3 py-2">
                On-chain anchoring isn't enabled yet — this template will use standard hash-based
                verification until a blockchain partner is connected.
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setView("list")}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Template
            </Button>
          </div>
        </div>

        {/* Live preview column */}
        <div className="lg:sticky lg:top-4 h-fit">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm font-medium text-foreground">Preview</p>
            <button
              onClick={() => setShowFullPreview(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              View full-size sample
            </button>
          </div>
          {renderCredentialCard("md")}
        </div>
      </div>

      {showFullPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShowFullPreview(false)}
        >
          <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowFullPreview(false)}
              className="absolute -top-10 right-0 flex items-center gap-1 text-sm text-white/80 hover:text-white"
            >
              <Icon name="X" size={16} />
              Close
            </button>
            {renderCredentialCard("lg")}
          </div>
        </div>
      )}
    </div>
  )
}

export default CredentialTemplatesPanel